<template>
  <div class="personal-info-container">
    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载用户数据...</p>
    </div>

    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchUserProfile" class="retry-btn">重试</button>
    </div>

    <!-- 未登录提示 -->
    <div v-if="!isAuthenticated" class="not-logged-in">
      <h2>请先登录</h2>
      <p>您需要登录后才能访问个人中心</p>
      <router-link to="/login" class="login-btn">去登录</router-link>
    </div>

    <template v-else>
      <!-- 个人信息展示与更新卡片 -->
      <section class="card info-card">
        <h2 class="card-title">个人信息</h2>
        <form @submit.prevent="updateProfile">
          <div class="form-group">
            <label for="name">用户名</label>
            <input id="name" type="text" v-model="profile.name" placeholder="请输入用户名" />
          </div>
          <!-- 单位类型（下拉菜单） -->
          <div class="form-group">
            <label for="profession">单位类型</label>
            <select id="profession" v-model="profile.profession">
              <option value="">请选择单位类型</option>
              <option value="科研院所">科研院所</option>
              <option value="大专院校">大专院校</option>
              <option value="航天部门">航天部门</option>
              <option value="商业公司">商业公司</option>
              <option value="行业部委">行业部委</option>
              <option value="地方用户">地方用户</option>
            </select>
          </div>

          <!-- 单位名称 -->
          <div class="form-group">
            <label for="institution_name">单位名称</label>
            <input id="institution_name" type="text" v-model="profile.institution_name" placeholder="请输入具体单位名称，如：中国科学院遥感地球所" />
          </div>

          <!-- 个人学历（下拉菜单） -->
          <div class="form-group">
            <label for="organization">个人学历</label>
            <select id="organization" v-model="profile.organization">
              <option value="">请选择个人学历</option>
              <option value="博士">博士</option>
              <option value="硕士">硕士</option>
              <option value="学士">学士</option>
              <option value="专科">专科</option>
              <option value="在读研究生">在读研究生</option>
              <option value="在读本科生">在读本科生</option>
              <option value="在读专科生">在读专科生</option>
            </select>
          </div>

          <!-- 使用目的 -->
          <div class="form-group">
            <label for="purpose">使用目的</label>
            <textarea id="purpose" v-model="profile.purpose" placeholder="请输入使用目的" rows="3"></textarea>
          </div>

          <div class="form-group">
            <label for="email">电子邮箱</label>
            <input id="email" type="email" v-model="profile.email" placeholder="请输入电子邮箱" />
          </div>
          <div class="form-group">
            <label for="phone">手机号码</label>
            <input id="phone" type="text" v-model="profile.phone" placeholder="请输入手机号码" />
          </div>
          <button type="submit" class="btn" :disabled="isLoading">更新个人信息</button>
        </form>
      </section>

      <!-- 修改密码模块 -->
      <section class="card password-card">
        <h2 class="card-title">修改密码</h2>
        <form @submit.prevent="resetPassword">
          <div class="form-group">
            <label for="currentPassword">当前密码</label>
            <input
              id="currentPassword"
              type="password"
              v-model="passwordForm.currentPassword"
              placeholder="请输入当前密码"
              :class="{'input-error': errors.currentPassword}"
            />
            <span v-if="errors.currentPassword" class="error-text">{{ errors.currentPassword }}</span>
          </div>
          <div class="form-group">
            <label for="newPassword">新密码</label>
            <input
              id="newPassword"
              type="password"
              v-model="passwordForm.newPassword"
              placeholder="请输入新密码"
              :class="{'input-error': errors.newPassword}"
            />
            <span v-if="errors.newPassword" class="error-text">{{ errors.newPassword }}</span>
          </div>
          <div class="form-group">
            <label for="confirmPassword">确认新密码</label>
            <input
              id="confirmPassword"
              type="password"
              v-model="passwordForm.confirmPassword"
              placeholder="请再次输入新密码"
              :class="{'input-error': errors.confirmPassword}"
            />
            <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
          </div>
          <button type="submit" class="btn" :disabled="isLoading">修改密码</button>
        </form>
      </section>

      <!-- 用户反馈与回复板块 -->
      <section class="card feedback-card">
        <h2 class="card-title">欢迎吐槽</h2>
        <p class="feedback-desc">您的建议将帮助我们不断改进</p>
        
        <!-- 反馈回复提醒区域 -->
        <div v-if="userFeedback.hasFeedback" class="feedback-reply-section">
          <div class="feedback-status">
            <div class="status-badge" :class="getStatusClass(userFeedback.status)">
              {{ getStatusText(userFeedback.status) }}
            </div>
            <div class="feedback-time">
              提交时间：{{ formatDate(userFeedback.created_at) }}
            </div>
          </div>
          
          <div class="feedback-content">
            <h4>您的反馈：</h4>
            <p>{{ userFeedback.feedback }}</p>
          </div>
          
          <div v-if="userFeedback.reply" class="reply-content">
            <h4>管理员回复：</h4>
            <div class="reply-box">
              <p>{{ userFeedback.reply }}</p>
              <div class="reply-time">
                回复时间：{{ formatDate(userFeedback.updated_at) }}
              </div>
            </div>
          </div>
          
          <div v-else-if="userFeedback.status === 'completed'" class="no-reply">
            <p>您的反馈已处理完成，感谢您的建议！</p>
          </div>
          
          <div v-else class="processing-status">
            <p>您的反馈正在处理中，请耐心等待...</p>
          </div>
          
          <div class="divider"></div>
        </div>
        
        <!-- 提交新反馈表单 -->
        <form @submit.prevent="handleSubmitFeedback">
          <div class="form-group full-width">
            <label for="feedback">您的反馈</label>
            <textarea
              id="feedback"
              v-model="feedbackContent"
              placeholder="请在此填写您的反馈"
              rows="4"
              :class="{'input-error': errors.feedback}"
            ></textarea>
            <span v-if="errors.feedback" class="error-text">{{ errors.feedback }}</span>
          </div>
          <button type="submit" class="btn" :disabled="isLoading">提交反馈</button>
        </form>
      </section>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: "PersonalInfo",
  data() {
    return {
      // 个人信息表单数据
      profile: {
        name: "",
        profession: "",
        institution_name: "",
        organization: "",
        purpose: "",
        email: "",
        phone: ""
      },
      // 修改密码表单数据
      passwordForm: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      },
      // 反馈内容
      feedbackContent: "",
      // 用户反馈数据
      userFeedback: {
        hasFeedback: false,
        feedback: "",
        status: "",
        reply: "",
        created_at: null,
        updated_at: null
      },
      // 错误提示数据
      errors: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        feedback: ""
      }
    };
  },
  computed: {
    ...mapState('user', ['error']),
    ...mapGetters('user', ['isAuthenticated', 'currentUser', 'isLoading'])
  },
  methods: {
    ...mapActions('user', ['fetchUserProfile', 'updateUserProfile', 'changePassword', 'submitFeedback', 'getUserFeedback']),
    
    // 更新个人信息
    async updateProfile() {
      try {
        await this.updateUserProfile(this.profile);
        this.$message.success('个人信息更新成功！');
      } catch (error) {
        this.$message.error(error.message || '更新失败，请重试');
      }
    },
    
    // 修改密码
    async resetPassword() {
      // 清除错误提示
      this.errors.currentPassword = "";
      this.errors.newPassword = "";
      this.errors.confirmPassword = "";
      let valid = true;
      
      if (!this.passwordForm.currentPassword) {
        this.errors.currentPassword = "请输入当前密码";
        valid = false;
      }
      if (!this.passwordForm.newPassword) {
        this.errors.newPassword = "请输入新密码";
        valid = false;
      } else if (this.passwordForm.newPassword.length < 6) {
        this.errors.newPassword = "新密码至少6位";
        valid = false;
      }
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.errors.confirmPassword = "两次密码输入不一致";
        valid = false;
      }
      
      if (!valid) return;
      
      try {
        await this.changePassword({
          currentPassword: this.passwordForm.currentPassword,
          newPassword: this.passwordForm.newPassword
        });
        
        this.$message.success('密码修改成功，请重新登录');
        this.passwordForm.currentPassword = "";
        this.passwordForm.newPassword = "";
        this.passwordForm.confirmPassword = "";
        
        // 退出登录
        this.$store.dispatch('user/logout');
        this.$router.push('/login');
      } catch (error) {
        this.$message.error(error.message || '密码修改失败，请重试');
      }
    },
    
    // 提交反馈
    async handleSubmitFeedback() {
      // 清空错误提示
      this.errors.feedback = "";
      if (!this.feedbackContent.trim()) {
        this.errors.feedback = "反馈内容不能为空";
        return;
      }
      
      try {
        await this.submitFeedback({ content: this.feedbackContent });
        this.$message.success('反馈提交成功，谢谢您的建议！');
        this.feedbackContent = "";
        // 重新获取用户反馈信息
        await this.fetchUserFeedback();
      } catch (error) {
        this.$message.error(error.message || '提交失败，请重试');
      }
    },
    
    // 获取用户反馈信息
    async fetchUserFeedback() {
      try {
        const feedbackData = await this.getUserFeedback();
        this.userFeedback = feedbackData;
      } catch (error) {
        console.error('获取用户反馈失败:', error);
      }
    },
    
    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        'unread': 'status-unread',
        'read': 'status-read',
        'processing': 'status-processing',
        'completed': 'status-completed'
      };
      return statusMap[status] || 'status-default';
    },
    
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'unread': '未读',
        'read': '已读',
        'processing': '处理中',
        'completed': '已完成'
      };
      return statusMap[status] || status;
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // 初始化用户数据
    initUserData() {
      if (this.currentUser) {
        this.profile = {
          name: this.currentUser.name || "",
          profession: this.currentUser.profession || "",
          institution_name: this.currentUser.institution_name || "",
          organization: this.currentUser.organization || "",
          purpose: this.currentUser.purpose || "",
          email: this.currentUser.email || "",
          phone: this.currentUser.phone || ""
        };
      }
    }
  },
  created() {
    // 如果已登录，获取用户信息
    if (this.isAuthenticated) {
      this.fetchUserProfile().then(() => {
        this.initUserData();
        // 获取用户反馈信息
        this.fetchUserFeedback();
      });
    }
  },
  watch: {
    // 监听currentUser变化，更新表单数据
    currentUser: {
      handler(newVal) {
        if (newVal) {
          this.initUserData();
        }
      },
      immediate: true
    }
  }
};
</script>
  
<style scoped>
/* ========== 变量 ========== */
:root {
  --bg: #f4f7fc;
  --primary: #0052cc;
  --secondary: #2684ff;
  --text: #333;
  --input-border: #c1c7d0;
  --error: #d72638;
  --success: #36b37e;
  --card-bg: #fff;
}

/* ========== 整体布局 ========== */
.personal-info-container {
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
  padding: 0.5rem 2rem 3rem 2rem;
  max-width: 1200px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  font-family: Arial, sans-serif;
  color: var(--text);
  min-height: 100vh;
  justify-content: flex-start;
}

/* ========== 卡片通用样式 ========== */
.card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  margin: 0px 50px 20px 18%;
  max-width: 700px;
  top: 15vh;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-left: 4px solid transparent;
  position: relative;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.card:hover::after {
  opacity: 1;
}

/* 标题样式 */
.card-title {
  font-size: 1.8rem;
  color: var(--primary);
  margin-bottom: 1rem;
  text-align: center;
}

/* 表单组样式 */
.form-group {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #000;
}
.form-group select {
  padding: 0.5rem;
  border: 1px solid var(--input-border);
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
}
.form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 5px rgba(0,82,204,0.5);
}

:root {
  --input-border: #ccc;
  --primary: #0052cc;
  --secondary: #003399;
  --error: #ff3860;
  --text: #333;
}

.form-group input,
.form-group textarea {
  padding: 0.5rem;
  border: 1px solid var(--input-border);
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 5px rgba(0, 82, 204, 0.5);
}

/* 个人信息模块表单焦点样式 */
.info-card .form-group input:focus,
.info-card .form-group textarea:focus,
.info-card .form-group select:focus {
  border-color: #0052cc;
  box-shadow: 0 0 5px rgba(0, 82, 204, 0.3);
}

/* 修改密码模块表单焦点样式 */
.password-card .form-group input:focus {
  border-color: #ff6b35;
  box-shadow: 0 0 5px rgba(255, 107, 53, 0.3);
}

/* 反馈模块表单焦点样式 */
.feedback-card .form-group textarea:focus {
  border-color: #36b37e;
  box-shadow: 0 0 5px rgba(54, 179, 126, 0.3);
}

.input-error {
  border-color: var(--error) !important;
}

.error-text {
  color: var(--error);
  font-size: 0.9rem;
  margin-top: 0.3rem;
}

/* 按钮样式 */
.btn {
  background: linear-gradient(45deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  width: 100%;
  margin-top: 1rem;
  position: relative;
  z-index: 1;
}

.btn:hover {
  background: var(--secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 82, 204, 0.3);
}

.btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 个人信息模块按钮 */
.info-card .btn {
  background: linear-gradient(45deg, #0052cc, #2684ff);
}

.info-card .btn:hover {
  background: linear-gradient(45deg, #2684ff, #0052cc);
  box-shadow: 0 4px 12px rgba(0, 82, 204, 0.3);
}

/* 修改密码模块按钮 */
.password-card .btn {
  background: linear-gradient(45deg, #ff6b35, #ff8c42);
}

.password-card .btn:hover {
  background: linear-gradient(45deg, #ff8c42, #ff6b35);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

/* 反馈模块按钮 */
.feedback-card .btn {
  background: linear-gradient(45deg, #36b37e, #48bb78);
}

.feedback-card .btn:hover {
  background: linear-gradient(45deg, #48bb78, #36b37e);
  box-shadow: 0 4px 12px rgba(54, 179, 126, 0.3);
}

/* ========== 个人信息模块 ========== */
.info-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left-color: #0052cc;
  position: relative;
  overflow: hidden;
}

.info-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: linear-gradient(45deg, rgba(0, 82, 204, 0.15), rgba(38, 132, 255, 0.15));
  border-radius: 50%;
  transform: translate(0px, -30px);
  z-index: 0;
}

.info-card .card-title {
  color: #0052cc;
  position: relative;
  z-index: 1;
}

.info-card .form-group {
  position: relative;
  z-index: 1;
}

.info-card .form-group input[readonly] {
  background: rgba(255, 255, 255, 0.7);
}

/* ========== 修改密码模块 ========== */
.password-card {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left-color: #ff6b35;
  position: relative;
  overflow: hidden;
}

.password-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(255, 107, 53, 0.15), rgba(255, 165, 0, 0.15));
  border-radius: 50%;
  transform: translate(20px, -20px);
  z-index: 0;
}

.password-card .card-title {
  color: #ff6b35;
  position: relative;
  z-index: 1;
}

.password-card .form-group {
  position: relative;
  z-index: 1;
}

.password-card .form-group input {
  margin-bottom: 0.75rem;
}

/* ========== 欢迎吐槽反馈模块 ========== */
.feedback-card {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-left-color: #36b37e;
  position: relative;
  overflow: hidden;
  margin-bottom: 5rem;
}

.feedback-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 90px;
  height: 90px;
  background: linear-gradient(45deg, rgba(54, 179, 126, 0.15), rgba(72, 187, 120, 0.15));
  border-radius: 50%;
  transform: translate(0px, -30px);
  z-index: 0;
}

.feedback-card .card-title {
  color: #36b37e;
  position: relative;
  z-index: 1;
}

.feedback-card .form-group {
  position: relative;
  z-index: 1;
}

.feedback-card .form-group.full-width {
  width: 100%;
}

.feedback-card textarea {
  resize: vertical;
  min-height: 80px;
}

.feedback-card .feedback-desc {
  font-size: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #2e7d32;
  position: relative;
  z-index: 1;
}

/* 反馈回复区域样式 */
.feedback-reply-section {
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
}

.feedback-reply-section .feedback-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.feedback-reply-section .status-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: center;
  min-width: 80px;
}

.feedback-reply-section .status-badge.status-unread {
  background: linear-gradient(45deg, #ff9800, #ffb74d);
}

.feedback-reply-section .status-badge.status-read {
  background: linear-gradient(45deg, #2196f3, #64b5f6);
}

.feedback-reply-section .status-badge.status-processing {
  background: linear-gradient(45deg, #9c27b0, #ba68c8);
}

.feedback-reply-section .status-badge.status-completed {
  background: linear-gradient(45deg, #4caf50, #81c784);
}

.feedback-reply-section .feedback-time {
  font-size: 0.9rem;
  color: #2e7d32;
  font-weight: 500;
}

.feedback-reply-section .feedback-content,
.feedback-reply-section .reply-content {
  margin-bottom: 1.5rem;
}

.feedback-reply-section .feedback-content h4,
.feedback-reply-section .reply-content h4 {
  color: #2e7d32;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.feedback-reply-section .feedback-content p {
  background: rgba(255, 255, 255, 0.8);
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #36b37e;
  margin: 0;
  line-height: 1.6;
  color: #000;
}

.feedback-reply-section .reply-box {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #ff9800;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.feedback-reply-section .reply-box p {
  margin: 0 0 0.5rem 0;
  line-height: 1.6;
  color: #0a0a0a;
}

.feedback-reply-section .reply-time {
  font-size: 0.85rem;
  color: #f57c00;
  font-weight: 500;
  text-align: right;
}

.feedback-reply-section .no-reply,
.feedback-reply-section .processing-status {
  text-align: center;
  color: #2e7d32;
  font-weight: 500;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
}

.feedback-reply-section .processing-status {
  color: #1976d2;
  background: rgba(33, 150, 243, 0.1);
}

/* 分隔线样式 */
.feedback-reply-section .divider {
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(54, 179, 126, 0.3), transparent);
  margin: 1.5rem 0;
  border-radius: 1px;
}

/* ========== 加载状态 ========== */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--primary);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== 错误提示 ========== */
.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.retry-btn {
  background: #c62828;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
}

/* ========== 未登录提示 ========== */
.not-logged-in {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  padding: 2.5rem;
  margin: 0 auto;
  max-width: 500px;
  text-align: center;
}

.not-logged-in h2 {
  color: var(--primary);
  margin-bottom: 1rem;
}

.not-logged-in p {
  margin-bottom: 1.5rem;
  color: var(--text);
}

.login-btn {
  display: inline-block;
  background: linear-gradient(45deg, var(--primary), var(--secondary));
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  transition: background 0.3s ease;
}

.login-btn:hover {
  background: var(--secondary);
}

/* ========== 响应式处理 ========== */
@media (max-width: 768px) {
  .card {
    width: 90%;
    padding: 1rem;
    margin: 0 auto 0rem auto;
    top: 0vh;
  }
  
  .card-title {
    font-size: 1.5rem;
  }
  
  .btn {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  
  /* 移动端优化装饰元素 */
  .info-card::before,
  .password-card::before,
  .feedback-card::before {
    width: 60px;
    height: 60px;
    transform: translate(15px, -15px);
  }
  
  /* 反馈回复区域移动端样式 */
  .feedback-reply-section .feedback-status {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .feedback-reply-section .status-badge {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
    min-width: 70px;
  }
  
  .feedback-reply-section .feedback-time {
    font-size: 0.8rem;
  }
  
  .feedback-reply-section .feedback-content h4,
  .feedback-reply-section .reply-content h4 {
    font-size: 1rem;
  }
  
  .feedback-reply-section .feedback-content p,
  .feedback-reply-section .reply-box {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
 
  
  .feedback-card {
    margin-bottom: 5rem;
  }
  
  .card-title {
    font-size: 1.3rem;
  }
  
  .form-group {
    margin-bottom: 0.75rem;
  }
  
  .form-group input,
  .form-group textarea,
  .form-group select {
    padding: 0.4rem;
    font-size: 0.9rem;
  }
  
  .btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
  }
  
  /* 超小屏幕隐藏装饰元素 */
  .info-card::before,
  .password-card::before,
  .feedback-card::before {
    display: none;
  }
  
  /* 反馈回复区域超小屏幕样式 */
  .feedback-reply-section .feedback-status {
    gap: 0.3rem;
  }
  
  .feedback-reply-section .status-badge {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
    min-width: 60px;
  }
  
  .feedback-reply-section .feedback-time {
    font-size: 0.75rem;
  }
  
  .feedback-reply-section .feedback-content h4,
  .feedback-reply-section .reply-content h4 {
    font-size: 0.9rem;
  }
  
  .feedback-reply-section .feedback-content p,
  .feedback-reply-section .reply-box {
    padding: 0.5rem;
    font-size: 0.85rem;
  }
  
  .feedback-reply-section .reply-time {
    font-size: 0.75rem;
  }
}
</style>
